import React, { useState, useEffect, useRef } from "react";
import { X, UploadCloud, AlertCircle } from "lucide-react";
import { getProofs, postUploadProof } from "../../../ApiHelper/url_Helpers";
import { toasterError, toasterSuccess } from "../../../Components/toaster";

const ProofSubmissionModal = ({ isOpen, onClose, regime: initialRegime }) => {
  const [activeTab, setActiveTab] = useState(initialRegime); // "new" or "old"
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchProofs();
    }
  }, [isOpen]);

  const fetchProofs = async () => {
    try {
      setLoading(true);
      const response = await getProofs();
      if (response && response.data) {
        setProofs(response.data);
      }
    } catch (error) {
      console.error("Error fetching proofs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toasterError("File size exceeds 5MB limit");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      // Optional: Add more fields to formData if backend requires them, e.g., section, item

      await postUploadProof(formData);
      toasterSuccess("Proof uploaded successfully");
      fetchProofs(); // Refresh proofs list
    } catch (error) {
      console.error("Error uploading proof:", error);
      toasterError("Failed to upload proof");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (!isOpen) return null;

  // Fallback to static list if API returns empty to demonstrate UI (can be removed later)
  const proofsOld = proofs.length > 0 ? proofs : [
    { section: "80C", item: "Employee Provident Fund", declared: "₹36,000", document: "payroll-auto.pdf", status: "Accepted", statusColor: "text-green-700 bg-green-100", dot: "bg-green-500", action: "Replace" },
    { section: "80C", item: "Public Provident Fund", declared: "₹50,000", document: "ppf-passbook.pdf", status: "Under review", statusColor: "text-orange-700 bg-orange-100", dot: "bg-orange-500", action: "Upload" },
    { section: "80C", item: "ELSS mutual funds", declared: "₹40,000", document: "—", status: "Not uploaded", statusColor: "text-red-700 bg-red-100", dot: "bg-red-500", action: "Upload" },
    { section: "80C", item: "Life insurance premium", declared: "₹24,000", document: "lic-receipt.pdf", status: "Accepted", statusColor: "text-green-700 bg-green-100", dot: "bg-green-500", action: "Replace" },
    { section: "80D", item: "Health insurance premium", declared: "₹50,000", document: "—", status: "Not uploaded", statusColor: "text-red-700 bg-red-100", dot: "bg-red-500", action: "Upload" },
    { section: "HRA", item: "Annual rent paid", declared: "₹4,80,000", document: "rent-receipts-q1.pdf", status: "Query raised", statusColor: "text-orange-700 bg-orange-100", dot: "bg-orange-500", action: "Reply" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Declarations</span>
            <h2 className="text-xl font-bold">Proof submission</h2>
            {loading && <span className="text-sm text-gray-500 ml-4">Loading...</span>}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab("new")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === "new" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
              >
                New Tax Regime
              </button>
              <button
                onClick={() => setActiveTab("old")}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === "old" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
              >
                Old Tax Regime
              </button>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* New Regime Alert */}
          {activeTab === "new" && (
            <div className="bg-red-50 border-b border-red-100 p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-red-700">No proofs are required on the new regime</h4>
                <p className="text-sm text-red-600 mt-0.5">
                  Nothing here affects your tax while you stay on the new regime. Uploads are retained in case you switch before 31 January.
                </p>
              </div>
            </div>
          )}

          <div className="p-6">
            {/* Top Stats & Dropzone */}
            <div className="flex gap-4 mb-8">
              <div className="flex-1 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 flex items-center justify-between p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-50 p-3 rounded-full text-blue-500">
                    <UploadCloud className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-800">Drop proof documents here</h4>
                    <p className="text-sm text-gray-400 mt-1">PDF or JPG, up to 5 MB each. Name files by secti...</p>
                  </div>
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileUpload} 
                  className="hidden" 
                  accept=".pdf, .jpg, .jpeg"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors"
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Browse files"}
                </button>
              </div>
              
              <div className="w-64 border border-gray-200 rounded-xl flex flex-col items-center justify-center p-6 bg-white">
                {activeTab === "new" ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-400 mb-1">Not required</h3>
                    <p className="text-xs text-gray-400 text-center">standard deduction<br/>applies automatically</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-3xl font-bold text-gray-800 mb-1">2 / 6</h3>
                    <p className="text-xs text-gray-500 text-center">proofs accepted of 6<br/>declared items</p>
                  </>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-gray-100">
                    <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-24">Section</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Item</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Declared</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Document</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {proofsOld.map((item, idx) => {
                    const isNew = activeTab === "new";
                    return (
                      <tr key={idx} className={isNew ? "opacity-60" : ""}>
                        <td className="py-4">
                          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded">
                            {item.section}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className="text-sm font-semibold text-gray-800">{item.item}</span>
                        </td>
                        <td className="py-4">
                          <span className="text-sm text-gray-500">{item.declared}</span>
                        </td>
                        <td className="py-4">
                          <span className="text-sm text-gray-400">{item.document}</span>
                        </td>
                        <td className="py-4">
                          {isNew ? (
                            <div className="w-24 h-6 bg-gray-200 rounded-full"></div>
                          ) : (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${item.statusColor}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${item.dot}`}></span>
                              {item.status}
                            </span>
                          )}
                        </td>
                        <td className="py-4 text-right">
                          <button className={`text-sm font-semibold hover:underline ${isNew ? "text-gray-400" : "text-blue-600"}`}>
                            {item.action}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProofSubmissionModal;
